import type { components } from '@/types/api-schema';
import { throwIfRateLimited } from '@/services/apiErrors';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Use auto-generated type from OpenAPI schema
export type UserProfileData = components['schemas']['UserProfile'];

export class ProfileNotFoundError extends Error {
  readonly status: number;

  constructor(message = 'User profile not found', status = 404) {
    super(message);
    this.name = 'ProfileNotFoundError';
    this.status = status;
  }
}

export class ProfileUnauthorizedError extends Error {
  readonly status: number;

  constructor(message = 'Unauthorized', status = 401) {
    super(message);
    this.name = 'ProfileUnauthorizedError';
    this.status = status;
  }
}

/**
 * アカウント削除リクエスト
 */
export interface DeleteAccountRequest {
  confirmation: string;
}

/**
 * アカウント削除レスポンス
 */
export interface DeleteAccountResponse {
  success: boolean;
  message: string;
  deletedEventsCount: number;
  cognitoDeletionSucceeded?: boolean;
}

/**
 * ユーザープロフィールを保存
 * @param token - Cognito JWT トークン
 * @param profile - プロフィールデータ
 */
export const saveUserProfile = async (
  token: string,
  profile: Partial<UserProfileData>
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(profile),
  });

  if (!response.ok) {
      throwIfRateLimited(response);
    const error = await response
      .json()
      .catch(() => ({ message: 'プロフィール保存に失敗しました' }));
    throw new Error(error.message || 'プロフィール保存に失敗しました');
  }
};

/**
 * アカウントを完全に削除
 * - ユーザープロフィール
 * - ユーザーが主催したイベント
 * - Cognitoアカウント
 * @param token - Cognito JWT トークン
 * @param request - 削除リクエスト（確認文字列）
 */
export const deleteAccount = async (
  token: string,
  request: DeleteAccountRequest
): Promise<DeleteAccountResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/user`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
      throwIfRateLimited(response);
    const error = await response
      .json()
      .catch(() => ({ message: 'アカウント削除に失敗しました' }));
    throw new Error(error.message || 'アカウント削除に失敗しました');
  }

  return response.json();
};
